import React, { useEffect } from 'react';
import styles from './ResourceSearch.module.scss';
import { Stack } from 'new/elements/Stack';
import { TextField } from 'new/elements/TextField';
import { Button } from '@mui/material';
import { SearchRounded } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import {
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_DATA_MAP,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_RECORD_TRANSFORM,
  RESOURCE_TYPE_SCHEMA_SPECS,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import {
  getDataCredential,
  getSchemaSpec,
  loadDataMap,
  loadDataSet,
  loadDataSink,
  loadDataSource,
  loadPipeline,
  clearLocalError,
  getCodeContainer,
} from 'js/actions';
import { useSelector } from 'react-redux';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { Autocomplete } from 'new/elements/Autocomplete';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import {
  GET_CODE_CONTAINER_FAILURE,
  GET_CREDENTIAL_FAILURE,
  GET_DATA_MAP_FAILURE,
  GET_DATA_SET_FAILURE,
  GET_DATA_SOURCE_FAILURE,
  GET_PIPELINES_FAILURE,
  GET_SCHEMA_SPEC_FAILURE,
  GET_SINK_FAILURE,
} from 'js/constants/actionTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';

export const resourceTypes = [
  {
    value: RESOURCE_TYPE_FLOW,
    action: loadPipeline,
    title: 'Flow',
  },
  {
    value: RESOURCE_TYPE_DATA_SET,
    action: loadDataSet,
    title: 'Nexset',
  },
  {
    value: RESOURCE_TYPE_SINK,
    action: loadDataSink,
    title: 'Destination',
  },
  {
    value: RESOURCE_TYPE_SOURCE,
    action: loadDataSource,
    title: 'Source',
  },
  {
    value: RESOURCE_TYPE_CREDENTIALS,
    action: getDataCredential,
    title: 'Credential',
  },
  {
    value: RESOURCE_TYPE_DATA_MAP,
    action: loadDataMap,
    title: 'Lookup',
  },
  {
    value: RESOURCE_TYPE_SCHEMA_SPECS,
    action: getSchemaSpec,
    title: 'Schema template',
  },
  {
    value: RESOURCE_TYPE_RECORD_TRANSFORM,
    action: getCodeContainer,
    title: 'Reusable transform',
  },
] as const;

export interface ResourceSearchProps {
  onSearch?: (data: { type: string; id: string }) => void;
  onChange?: (data: { type: string; id: string }) => void;
  buttonColor?: string;
  type?: string;
  id?: string;
  error?: {
    id?: string;
    type?: string;
  };
  ignoreErrors?: boolean;
  noRequest?: boolean;
  fullWidth?: boolean;
  hideOptions?: (typeof resourceTypes)[number]['value'][];
}

const localErrorTypes = [
  GET_PIPELINES_FAILURE,
  GET_DATA_SET_FAILURE,
  GET_SINK_FAILURE,
  GET_DATA_SOURCE_FAILURE,
  GET_CREDENTIAL_FAILURE,
  GET_DATA_MAP_FAILURE,
  GET_SCHEMA_SPEC_FAILURE,
  GET_CODE_CONTAINER_FAILURE,
];

export const ResourceSearch = (props: ResourceSearchProps) => {
  const {
    onSearch,
    onChange,
    buttonColor,
    type,
    id,
    noRequest,
    fullWidth,
    hideOptions,
    ignoreErrors,
  } = props;
  const dispatch = useAppDispatch();
  const localError = useSelector((state: any) => state.errors?.localError);

  const localErrorMessage =
    localError?.message && localErrorTypes.includes(localError?.type) ? localError?.message : null;

  const {
    register,
    watch,
    formState: { errors, isValid },
    setValue,
    control,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      resource: type || '',
      id,
    },
  });

  const values = watch(['resource', 'id']);

  useEffect(() => {
    if (localErrorMessage) {
      dispatch(clearLocalError());
    }
  }, []);

  useDidUpdateEffect(() => {
    setValue('resource', type);
    setValue('id', id);
  }, [type, id]);

  const onSubmit = (data) => {
    if (!noRequest) {
      const action = resourceTypes.find((r) => r.value === data.resource)?.action;
      // @ts-ignore
      dispatch(
        action(data.id, {
          ignoreErrors,
          aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
          isPreview: true,
        }),
      );
    }
    if (onSearch) {
      onSearch({ type: data.resource, id: data.id });
    }
  };

  const onChangeField = (e) => {
    if (localErrorMessage) {
      dispatch(clearLocalError());
    }
    if (typeof onChange === 'function') {
      const data = {
        ...(e.target.name === 'resource' ? { type: e.target.value } : { type: values[0] }),
        ...(e.target.name === 'id' ? { id: e.target.value } : { id: values[1] }),
      };
      onChange(data);
    }
  };

  const onChangeType = (field) => (_, data) => {
    if (!data) {
      onChangeField({ target: { name: 'resource', value: '' } });
      field.onChange('');
      return;
    }
    onChangeField({ target: { name: 'resource', value: data.value } });
    field.onChange(data.value);
  };

  return (
    <form onChange={onChangeField} onSubmit={!!onSearch ? handleSubmit(onSubmit) : undefined}>
      <Stack direction="row" gap="16px" alignItems="start">
        <Stack direction="row" gap="16px" flex={fullWidth ? 1 : undefined}>
          <Controller
            control={control}
            render={({ field }) => (
              <Autocomplete
                disableClearable
                size="small"
                {...field}
                sx={{
                  minWidth: 200,
                  ...(fullWidth ? { flex: 1 } : undefined),
                }}
                isOptionEqualToValue={(option: any, value) => {
                  if (typeof option === 'string') {
                    return option === value;
                  }
                  return option.value === value;
                }}
                getOptionLabel={(opt) => {
                  if (!opt) {
                    return '';
                  }
                  if (typeof opt === 'string') {
                    return resourceTypes.find((r) => r?.value === opt)?.title;
                  }
                  return opt.title;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Resource Type"
                    error={!!errors.resource}
                    helperText={errors.resource ? errors.resource?.message + '' : ''}
                  />
                )}
                onChange={onChangeType(field)}
                options={
                  hideOptions
                    ? resourceTypes.filter((type) => !hideOptions.includes(type.value))
                    : resourceTypes
                }
              />
            )}
            name="resource"
          />
          <TextField
            size="small"
            sx={{ ...(fullWidth ? { flex: 1 } : undefined) }}
            label={'Resource ID'}
            error={!!errors.id || !!localErrorMessage}
            helperText={
              errors.id || localErrorMessage ? (
                <>{localErrorMessage || errors.id?.message}</>
              ) : undefined
            }
            {...register('id', {
              required: 'Please type an id to search for',
              pattern: {
                value: /^[0-9]+$/,
                message: 'Please enter a number',
              },
            })}
          />
        </Stack>
        {!!onSearch && (
          <Button
            type="submit"
            className={styles.button}
            // @ts-ignore
            color={buttonColor || 'primary'}
            disabled={!isValid}
            variant="contained"
            startIcon={<SearchRounded />}
          >
            Find Resource
          </Button>
        )}
      </Stack>
    </form>
  );
};
